import React from "react";
import { TrendingUp, MapPin, Store, Building2, Clock } from "lucide-react";
import { formatGeneratedAt } from "../lib/datetime";

const layerColors = {
  category: "text-bm-cyan border-bm-cyan/40",
  behaviour: "text-bm-amber border-bm-amber/40",
  product: "text-bm-green border-bm-green/40",
};

export default function SignalCard({ signal, idx = 0, expanded = false }) {
  const layerCls = layerColors[signal.insight_layer] || "text-white border-white/30";
  const generatedLabel = formatGeneratedAt(signal.generated_at || signal.created_at);
  return (
    <div
      data-testid={`signal-card-${signal.id}`}
      className="bm-card fade-up p-4 flex flex-col gap-3 group"
      style={{ animationDelay: `${Math.min(idx * 40, 600)}ms` }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`label-mono px-1.5 py-0.5 border ${layerCls}`}>
            {signal.insight_layer}
          </span>
          <span className="label-mono text-bm-dim">{signal.source_platform}</span>
        </div>
        <div className="flex items-center gap-1 font-mono text-xs text-bm-green">
          <TrendingUp className="w-3 h-3" />
          <span>+{signal.velocity}</span>
        </div>
      </div>

      <h3 className="font-heading text-base font-medium leading-tight text-white group-hover:text-bm-cyan transition-colors" data-testid={`signal-title-${signal.id}`}>
        {signal.title}
      </h3>
      <p className={`text-sm text-bm-muted leading-relaxed ${expanded ? "" : "line-clamp-3"}`}>{signal.summary}</p>

      {expanded && signal.evidence_points?.length > 0 && (
        <div className="border-t border-white/5 pt-2" data-testid={`signal-evidence-${signal.id}`}>
          <div className="label-mono text-bm-dim mb-1.5">OBSERVED EVIDENCE</div>
          <ul className="space-y-1 text-xs text-bm-muted">
            {signal.evidence_points.map((point, index) => (
              <li key={`${signal.id}-evidence-${index}`} className="flex gap-2">
                <span className="text-bm-cyan">→</span><span>{point}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Velocity bar */}
      <div className="h-1 bg-white/5 relative overflow-hidden">
        <div className="velocity-bar h-full" style={{ width: `${signal.velocity}%` }}></div>
      </div>

      {/* Brands winning */}
      {signal.reference_brands && signal.reference_brands.length > 0 && (
        <div className="border-t border-white/5 pt-2" data-testid={`signal-brands-${signal.id}`}>
          <div className="flex items-center gap-1 label-mono text-bm-dim mb-1.5">
            <Building2 className="w-3 h-3" />WHO IS WINNING
          </div>
          <div className="flex flex-wrap gap-1">
            {signal.reference_brands.slice(0, 4).map((b) => (
              <span key={b} className="text-[11px] font-mono text-bm-amber border border-bm-amber/30 px-1.5 py-0.5">
                {b}
              </span>
            ))}
          </div>
        </div>
      )}

      {signal.winning_sellers && signal.winning_sellers.length > 0 && (
        <div data-testid={`signal-sellers-${signal.id}`}>
          <div className="flex items-center gap-1 label-mono text-bm-dim mb-1.5">
            <Store className="w-3 h-3" />SELLER SURFACES
          </div>
          <div className="flex flex-wrap gap-1">
            {signal.winning_sellers.slice(0, 3).map((s) => (
              <span key={s} className="text-[11px] font-mono text-bm-cyan border border-bm-cyan/30 px-1.5 py-0.5">
                {s}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between text-xs font-mono pt-1 border-t border-white/5">
        <div className="flex items-center gap-1 text-bm-muted">
          <MapPin className="w-3 h-3" />
          <span>{signal.geography}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-bm-dim">CONF</span>
          <span className={signal.confidence >= 85 ? "text-bm-green" : signal.confidence >= 70 ? "text-bm-amber" : "text-bm-danger"}>
            {signal.confidence}%
          </span>
        </div>
      </div>

      {/* Generated-at stamp */}
      <div className="flex items-center gap-1 text-[10px] font-mono text-bm-dim" data-testid={`signal-generated-at-${signal.id}`}>
        <Clock className="w-3 h-3" />
        <span>Generated {generatedLabel}</span>
      </div>

      {signal.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {signal.tags.slice(0, 4).map((t) => (
            <span key={t} className="text-[10px] font-mono text-bm-dim border border-white/10 px-1.5 py-0.5">
              #{t}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
